import {t} from 'i18next';
import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {CustomMenuItem} from '../../../common/arr-menu/ArrMenu';

dayjs.extend(utc);
import {formatUTCDateTime, numberFormatter} from '../../../../utils/utils';
import {
  docType,
  IGetAIReviewJobDetailsResponse,
  IGetAIReviewJobResultResponse,
} from '../../../../api/types/aiReview.type';
import {
  PROTOCOL_VERSION_DISPLAY_MODE,
  getParentProtocolVersionName,
  getProtocolVersionName,
  resolveProtocolVersionMode,
} from '../common/JobDetails/protocolVersionMode';
import {
  serializeProtocolPdfContent,
  type ProtocolPdfContent,
  type ProtocolPdfLine,
} from '../protocol-list/protocol-pdf/protocolPdf';

export enum AIReviewJobStatus {
  InProgress = 'in progress',
  Complete = 'completed',
  Failed = 'failed',
  Queued = 'queued',
  InQueue = 'in queue',
  Draft = 'draft',
}

export const tableProps = {
  ROW_HEIGHT: 36,
  FILTER_HEADER_HEIGHT: 48,
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

const status = [
  AIReviewJobStatus.InProgress,
  AIReviewJobStatus.Complete,
  AIReviewJobStatus.Failed,
  AIReviewJobStatus.Queued,
  AIReviewJobStatus.InQueue,
  AIReviewJobStatus.Draft,
];
const defaultTextClass: Record<AIReviewJobStatus, string> = Object.fromEntries(
  status.map((item) => [item, 'text-headings'])
) as Record<AIReviewJobStatus, string>;

const defaultNumClass: Record<AIReviewJobStatus, string> = Object.fromEntries(
  status.map((item) => [
    item,
    item === 'draft' ? 'text-body-subtle' : 'text-action-default',
  ])
) as Record<AIReviewJobStatus, string>;

export interface IColumnsData {
  field: string;
  headerName: string;
  fieldDataKey: string;
  filterable: boolean;
  width: number;
  textColorClass: {[key in AIReviewJobStatus]: string};
  align?: string;
  clickable?: {[key in AIReviewJobStatus]: boolean};
  sortable?: boolean;
  formatFunction?: (...args: any[]) => string | number | null | undefined;
}

enum docTypeKeys {
  totalDocCount = 'totalDocCount',
  completedDocCount = 'completedDocCount',
  excludedErrorDocCount = 'excludedErrorDocCount',
}
export const docTypesMapping: {[key: string]: docType} = {
  [docTypeKeys.totalDocCount]: 'all',
  [docTypeKeys.completedDocCount]: 'completed',
  [docTypeKeys.excludedErrorDocCount]: 'erroredexcluded',
};

function formatNumber(text: string, status: string) {
  const formattedText = text ? numberFormatter.format(Number(text)) : text;
  return status?.toLowerCase() === AIReviewJobStatus.Draft &&
    Number(formattedText) === 0
    ? '---'
    : formattedText;
}
export function formatDate(dateString: string | null | undefined): string {
  return dateString?.trim() ? formatUTCDateTime(dateString) : '---';
}

function formatStatus(status: string) {
  return status.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
}

enum AiReviewJobType {
  ISSUECODE = 'Issue Coding',
  RELEVANCY = 'Relevancy',
}
function jobTypeMapping(jobType: string): string {
  return AiReviewJobType?.[jobType as keyof typeof AiReviewJobType] ?? jobType;
}

export function formatGroupName(groupName: string | null | undefined): string {
  return groupName?.trim() ? groupName : '---';
}

export function formatSourceName(
  sourceName: string | null | undefined
): string {
  return sourceName?.trim() ? sourceName : '---';
}

export const columnsData: IColumnsData[] = [
  {
    field: 'ReviewJobID',
    headerName: t('ai.review.jobList.jobListColumns.rowId'),
    fieldDataKey: 'jobId',
    filterable: false,
    width: 70,
    textColorClass: defaultTextClass,
    align: 'center',
  },
  {
    field: 'JobName',
    headerName: t('ai.review.jobList.jobListColumns.jobName'),
    fieldDataKey: 'jobName',
    filterable: true,
    width: 260,
    textColorClass: {
      ...defaultTextClass,
      [AIReviewJobStatus.Complete]: 'text-action-default',
    },
    clickable: {
      [AIReviewJobStatus.InProgress]: false,
      [AIReviewJobStatus.Complete]: true,
      [AIReviewJobStatus.Failed]: false,
      [AIReviewJobStatus.Queued]: false,
      [AIReviewJobStatus.InQueue]: false,
      [AIReviewJobStatus.Draft]: false,
    },
  },
  {
    field: 'JobType',
    headerName: t('ai.review.jobList.jobListColumns.type'),
    fieldDataKey: 'jobType',
    filterable: true,
    width: 140,
    textColorClass: defaultTextClass,
    formatFunction: jobTypeMapping,
  },
  {
    field: 'ProtocolVersion',
    headerName: t('ai.review.jobList.jobListColumns.protocolVersion'),
    fieldDataKey: 'protocolVersion',
    filterable: true,
    width: 150,
    textColorClass: defaultTextClass,
  },
  {
    field: 'Source',
    headerName: t('ai.review.jobList.jobListColumns.source'),
    fieldDataKey: 'sourceName',
    filterable: true,
    width: 190,
    textColorClass: defaultTextClass,
    formatFunction: formatSourceName,
  },
  {
    field: 'Status',
    headerName: t('ai.review.jobList.jobListColumns.status'),
    fieldDataKey: 'status',
    filterable: true,
    width: 120,
    formatFunction: formatStatus,
    textColorClass: {
      ...defaultTextClass,
      [AIReviewJobStatus.InProgress]: 'text-notice',
      [AIReviewJobStatus.Complete]: 'text-success',
      [AIReviewJobStatus.Failed]: 'text-critical',
      [AIReviewJobStatus.Draft]: 'text-body-subtle',
    },
  },
  {
    field: docTypeKeys.totalDocCount,
    headerName: t('ai.review.jobList.jobListColumns.totalDocs'),
    fieldDataKey: docTypeKeys.totalDocCount,
    filterable: false,
    sortable: false,
    width: 107,
    textColorClass: defaultNumClass,
    formatFunction: formatNumber,
    clickable: {
      [AIReviewJobStatus.InProgress]: true,
      [AIReviewJobStatus.Complete]: true,
      [AIReviewJobStatus.Failed]: true,
      [AIReviewJobStatus.Queued]: true,
      [AIReviewJobStatus.InQueue]: true,
      [AIReviewJobStatus.Draft]: false,
    },
  },
  {
    field: docTypeKeys.completedDocCount,
    headerName: t('ai.review.jobList.jobListColumns.completed'),
    fieldDataKey: docTypeKeys.completedDocCount,
    filterable: false,
    sortable: false,
    width: 127,
    textColorClass: {
      ...defaultNumClass,
      [AIReviewJobStatus.Failed]: 'text-headings',
    },
    formatFunction: formatNumber,
    clickable: {
      [AIReviewJobStatus.InProgress]: true,
      [AIReviewJobStatus.Complete]: true,
      [AIReviewJobStatus.Failed]: true,
      [AIReviewJobStatus.Queued]: true,
      [AIReviewJobStatus.InQueue]: true,
      [AIReviewJobStatus.Draft]: false,
    },
  },
  {
    field: docTypeKeys.excludedErrorDocCount,
    headerName: t('ai.review.jobList.jobListColumns.excludedError'),
    fieldDataKey: docTypeKeys.excludedErrorDocCount,
    filterable: false,
    sortable: false,
    width: 180,
    textColorClass: {
      ...defaultNumClass,
      [AIReviewJobStatus.Failed]: 'text-critical',
    },
    formatFunction: formatNumber,
    clickable: {
      [AIReviewJobStatus.InProgress]: true,
      [AIReviewJobStatus.Complete]: true,
      [AIReviewJobStatus.Failed]: true,
      [AIReviewJobStatus.Queued]: true,
      [AIReviewJobStatus.InQueue]: true,
      [AIReviewJobStatus.Draft]: false,
    },
  },
  {
    field: 'CreatedOn',
    headerName: t('ai.review.jobList.jobListColumns.createdDate'),
    fieldDataKey: 'createdDate',
    filterable: false,
    width: 180,
    textColorClass: defaultTextClass,
    formatFunction: formatDate,
  },
  {
    field: 'ExecutedDate',
    headerName: t('ai.review.jobList.jobListColumns.executedDate'),
    fieldDataKey: 'executionDate',
    filterable: false,
    width: 180,
    textColorClass: defaultTextClass,
    formatFunction: formatDate,
  },
  {
    field: 'CreatedBy',
    headerName: t('ai.review.jobList.jobListColumns.createdBy'),
    fieldDataKey: 'createdBy',
    filterable: true,
    width: 160,
    textColorClass: defaultTextClass,
  },
  {
    field: 'JobGroupName',
    headerName: t('ai.review.jobList.jobListColumns.group'),
    fieldDataKey: 'jobGroupName',
    filterable: true,
    sortable: true,
    width: 180,
    textColorClass: defaultTextClass,
    formatFunction: formatGroupName,
  },
];

const KNOWN_JOB_STATUSES = new Set<string>(Object.values(AIReviewJobStatus));

const STATUS_ALIAS_MAP = new Map<string, AIReviewJobStatus>([
  ['in progress', AIReviewJobStatus.InProgress],
  ['inprogress', AIReviewJobStatus.InProgress],
  ['in-progress', AIReviewJobStatus.InProgress],
  ['in_progress', AIReviewJobStatus.InProgress],
  ['draft', AIReviewJobStatus.Draft],
  ['complete', AIReviewJobStatus.Complete],
  ['completed', AIReviewJobStatus.Complete],
  ['failed', AIReviewJobStatus.Failed],
  ['processing', AIReviewJobStatus.InProgress],
  ['queued', AIReviewJobStatus.Queued],
  ['pending', AIReviewJobStatus.InQueue],
  ['inqueue', AIReviewJobStatus.InQueue],
  ['in queue', AIReviewJobStatus.InQueue],
  ['in-queue', AIReviewJobStatus.InQueue],
  ['in_queue', AIReviewJobStatus.InQueue],
]);

/**
 * Maps grid/API job status values to {@link AIReviewJobStatus}.
 * Returns null when the value cannot be matched (empty menu for that row).
 */
export const parseJobListRowStatus = (
  raw: unknown
): AIReviewJobStatus | null => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (KNOWN_JOB_STATUSES.has(s)) {
    return s as AIReviewJobStatus;
  }
  const norm = s.toLowerCase().replace(/\s+/g, ' ');
  return STATUS_ALIAS_MAP.get(norm) ?? null;
};

export const getJobListMenuItems = (
  status: AIReviewJobStatus | string | null
): CustomMenuItem[] => {
  const normalizedStatus = parseJobListRowStatus(status);
  const exportJobDetailsItem: CustomMenuItem = {
    id: 'exportJobDetailsAsPdf',
    label: '',
    value: 'exportJobDetailsAsPdf',
  };

  if (normalizedStatus === AIReviewJobStatus.InProgress) {
    return [
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider', label: '', value: 'divider', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  if (normalizedStatus === AIReviewJobStatus.Failed) {
    return [
      {id: 'retry', label: '', value: 'retry'},
      {id: 'cloneJob', label: '', value: 'cloneJob'},
      exportJobDetailsItem,
      {id: 'divider-1', label: '', value: 'divider-1', variant: 'divider'},
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider-2', label: '', value: 'divider-2', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  if (normalizedStatus === AIReviewJobStatus.Draft) {
    return [
      {id: 'edit', label: '', value: 'edit'},
      {id: 'cloneJob', label: '', value: 'cloneJob'},
      exportJobDetailsItem,
      {id: 'divider-1', label: '', value: 'divider-1', variant: 'divider'},
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider-2', label: '', value: 'divider-2', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  if (
    normalizedStatus === AIReviewJobStatus.Complete ||
    normalizedStatus === AIReviewJobStatus.InQueue
  ) {
    return [
      {id: 'cloneJob', label: '', value: 'cloneJob'},
      exportJobDetailsItem,
      {id: 'divider-1', label: '', value: 'divider-1', variant: 'divider'},
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider-2', label: '', value: 'divider-2', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  return [];
};

const PDF_MARGIN = 15;
const EMPTY_PDF_VALUE = '---';

const sanitizeMultilineText = (text: string): string =>
  text
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizePdfValue = (value: unknown): string => {
  if (value === null || value === undefined) return EMPTY_PDF_VALUE;

  if (typeof value === 'string') {
    const sanitized = sanitizeMultilineText(value);
    return sanitized || EMPTY_PDF_VALUE;
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }

  return EMPTY_PDF_VALUE;
};

const PDF_DATE_FORMAT = 'DD-MM-YYYY HH:mm:ss';

// Format ISO date to "DD-MM-YYYY HH:MM:SS" in UTC for existing callers.
export const formatDateTimeUTC = (iso?: string): string => {
  if (!iso) return '';

  const d = dayjs.utc(iso);
  if (!d.isValid()) return '';

  return d.format(PDF_DATE_FORMAT);
};

// Format an export timestamp in the browser's local timezone.
export const formatDateTimeLocal = (date?: string | Date): string => {
  if (!date) return '';

  const d = dayjs(date);
  if (!d.isValid()) return '';

  return d.format(PDF_DATE_FORMAT);
};

const formatExportTimestamp = (exportedAt?: string | Date): string => {
  return formatDateTimeLocal(exportedAt ?? new Date()) || EMPTY_PDF_VALUE;
};

const getJobTypeLabel = (jobType: unknown): string => {
  const normalizedType = String(jobType ?? '').toUpperCase();
  if (normalizedType === 'ISSUECODE') return 'Issue Coding';
  if (normalizedType === 'RELEVANCY') return 'Relevancy';
  return normalizePdfValue(jobType);
};

type PdfRecord = Record<string, unknown>;

const isPdfRecord = (value: unknown): value is PdfRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getNamePart = (value: unknown): string =>
  typeof value === 'string' ? sanitizeMultilineText(value) : '';

const joinNameParts = (firstName: unknown, lastName: unknown): string =>
  [getNamePart(firstName), getNamePart(lastName)].filter(Boolean).join(' ');

const formatCreatedByText = (value: string): string => {
  const normalized = sanitizeMultilineText(value);
  if (!normalized) return EMPTY_PDF_VALUE;

  const nameParts = normalized.split(',').map((part) => part.trim());
  if (nameParts.length === 2 && nameParts.every(Boolean)) {
    return `${nameParts[1]} ${nameParts[0]}`;
  }

  return normalized.replace(/,\s*/g, ' ');
};

const getCreatedByLabel = (jobData: IGetAIReviewJobDetailsResponse): string => {
  const details = jobData as IGetAIReviewJobDetailsResponse & {
    createdBy?: unknown;
    firstName?: unknown;
    lastName?: unknown;
  };

  const apiName = joinNameParts(details.firstName, details.lastName);
  if (apiName) return apiName;

  if (typeof details.createdBy === 'string') {
    return formatCreatedByText(details.createdBy);
  }

  if (isPdfRecord(details.createdBy)) {
    const createdByObject = details.createdBy;
    const objectName = joinNameParts(
      createdByObject.firstName ?? createdByObject.first_name,
      createdByObject.lastName ?? createdByObject.last_name
    );
    if (objectName) return objectName;

    for (const key of ['fullName', 'name', 'displayName']) {
      if (typeof createdByObject[key] === 'string') {
        return formatCreatedByText(createdByObject[key] as string);
      }
    }
  }

  return EMPTY_PDF_VALUE;
};

const getProtocolLabel = (jobData: IGetAIReviewJobDetailsResponse): string => {
  const displayMode = resolveProtocolVersionMode(jobData);
  const protocolVersionName = getProtocolVersionName(jobData);
  const parentProtocolVersionName = getParentProtocolVersionName(jobData);

  if (displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MANUAL) {
    return 'Manual';
  }

  if (
    displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVE_AS_NEW ||
    displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVED
  ) {
    const modifiedName =
      jobData.protocolName || parentProtocolVersionName || protocolVersionName;
    const normalizedModifiedName = normalizePdfValue(modifiedName);

    return `${normalizedModifiedName} - Modified`;
  }

  return normalizePdfValue(protocolVersionName || jobData.protocolName);
};

const getSourceLabel = (jobData: IGetAIReviewJobDetailsResponse): string => {
  const sourceValues = [jobData.sourceType, jobData.sourceName]
    .map((value) => normalizePdfValue(value))
    .filter((value) => value !== EMPTY_PDF_VALUE);

  return sourceValues.length > 0 ? sourceValues.join(' - ') : EMPTY_PDF_VALUE;
};

const getErroredCount = (
  jobResult: IGetAIReviewJobResultResponse
): number | undefined => jobResult.errored ?? jobResult.Errored;

// Render a label/value pair with proper wrapping.
function drawLabelValue(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number {
  const labelText = `${label}: `;
  let cursorY = ensureSpace(doc, lineHeight, y, PDF_MARGIN);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text(labelText, x, cursorY);

  const labelWidth = doc.getTextWidth(labelText);

  const wrapped = doc.splitTextToSize(
    normalizePdfValue(value),
    Math.max(10, maxWidth - labelWidth)
  );
  const valueLines = wrapped.length > 0 ? wrapped : [EMPTY_PDF_VALUE];

  valueLines.forEach((line, index) => {
    if (index > 0) {
      cursorY += lineHeight;
      cursorY = ensureSpace(doc, lineHeight, cursorY, PDF_MARGIN);
    }
    doc.text(line, x + labelWidth, cursorY);
  });

  return cursorY;
}

//Ensures enough vertical space exists.
// Adds new page if required and returns updated Y position.
function ensureSpace(
  doc: jsPDF,
  needed: number,
  currentY: number,
  margin: number
): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + needed > pageHeight - margin) {
    doc.addPage();
    return margin;
  }
  return currentY;
}

function drawSectionHeading(
  doc: jsPDF,
  heading: string,
  x: number,
  y: number,
  lineHeight: number,
  margin: number
): number {
  const headingY = ensureSpace(doc, lineHeight * 2, y, margin);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(heading, x, headingY);
  return headingY + lineHeight;
}

function drawProtocolField(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number {
  const labelText = `${label}:`;
  const valueGap = 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  const labelWidth = doc.getTextWidth(labelText);
  const renderValueInline = labelWidth + valueGap + 24 <= maxWidth;
  const valueX = renderValueInline ? x + labelWidth + valueGap : x + 6;
  const valueWidth = Math.max(
    renderValueInline ? maxWidth - labelWidth - valueGap : maxWidth - 6,
    20
  );
  doc.setFont('helvetica', 'normal');
  const wrappedValues = doc.splitTextToSize(value, valueWidth) as string[];
  const values = wrappedValues.length > 0 ? wrappedValues : [EMPTY_PDF_VALUE];
  let cursorY = ensureSpace(doc, lineHeight, y, PDF_MARGIN);

  const drawLabel = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(labelText, x, cursorY);
  };

  if (!renderValueInline) {
    drawLabel();
    cursorY += lineHeight;
  }

  values.forEach((wrappedValue, index) => {
    cursorY = ensureSpace(doc, lineHeight, cursorY, PDF_MARGIN);
    if (renderValueInline && index === 0) {
      drawLabel();
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(wrappedValue, valueX, cursorY);
    cursorY += lineHeight;
  });

  return cursorY;
}

function drawProtocolPdfLine(
  doc: jsPDF,
  line: ProtocolPdfLine,
  y: number,
  contentWidth: number,
  lineHeight: number
): number {
  if (line.style === 'spacer') {
    return y + 2;
  }

  const x = PDF_MARGIN + line.level * 6;
  const width = Math.max(contentWidth - line.level * 6, 20);

  if (line.style === 'section' || line.style === 'heading') {
    const cursorY = ensureSpace(doc, lineHeight, y, PDF_MARGIN);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(line.style === 'section' ? 13 : 12);
    doc.text(line.text, x, cursorY);
    return cursorY + lineHeight;
  }

  if (line.label && line.value !== undefined) {
    return drawProtocolField(
      doc,
      line.label,
      line.value,
      x,
      y,
      width,
      lineHeight
    );
  }

  const cursorY = ensureSpace(doc, lineHeight, y, PDF_MARGIN);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  const textLines = doc.splitTextToSize(line.text, width);
  const lines = textLines.length > 0 ? textLines : [EMPTY_PDF_VALUE];
  let nextY = cursorY;

  lines.forEach((textLine: string) => {
    nextY = ensureSpace(doc, lineHeight, nextY, PDF_MARGIN);
    doc.text(textLine, x, nextY);
    nextY += lineHeight;
  });

  return nextY;
}

export function exportAIReviewJobDetailsPdf(
  jobData: IGetAIReviewJobDetailsResponse,
  jobResult?: IGetAIReviewJobResultResponse,
  exportedAt?: string | Date,
  protocolContent?: ProtocolPdfContent
): void {
  const doc = new jsPDF();

  const margin = PDF_MARGIN;
  const lineHeight = 7;
  const contentWidth = doc.internal.pageSize.getWidth() - margin * 2;

  let y = margin + 3;

  const title = `Job Name: ${normalizePdfValue(jobData.jobName)}`;
  const titleLines = doc.splitTextToSize(title, contentWidth);
  const renderedTitleLines = titleLines.length > 0 ? titleLines : [title];

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  renderedTitleLines.forEach((line) => {
    y = ensureSpace(doc, lineHeight + 2, y, margin);
    doc.text(line, margin, y);
    y += lineHeight;
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  y = drawLabelValue(
    doc,
    'Date & Time',
    formatExportTimestamp(exportedAt),
    margin,
    y,
    contentWidth,
    lineHeight
  );
  y += lineHeight;

  y = drawSectionHeading(
    doc,
    'Details:',
    margin,
    y + lineHeight,
    lineHeight,
    margin
  );

  const jobDetailsFields: Array<{label: string; value: unknown}> = [
    {label: 'Job ID', value: jobData.jobId},
    {label: 'Type', value: getJobTypeLabel(jobData.jobType)},
    {label: 'Created By', value: getCreatedByLabel(jobData)},
    {label: 'Status', value: formatStatus(String(jobData.status ?? ''))},
    {label: 'Source', value: getSourceLabel(jobData)},
  ];

  jobDetailsFields.forEach(({label, value}) => {
    y = drawLabelValue(
      doc,
      label,
      normalizePdfValue(value),
      margin,
      y,
      contentWidth,
      lineHeight
    );
    y += lineHeight;
  });

  const isCompleted =
    parseJobListRowStatus(jobData.status) === AIReviewJobStatus.Complete;
  if (isCompleted) {
    if (!jobResult) {
      throw new Error('Job summary data is required for completed exports');
    }

    y = drawSectionHeading(
      doc,
      'Summary:',
      margin,
      y + lineHeight,
      lineHeight,
      margin
    );

    const summaryFields: Array<{label: string; value: unknown}> = [
      {
        label: 'Documents in selected source',
        value: jobResult.documentsInSelectedSource,
      },
      {label: 'Excluded from AI', value: jobResult.excludedFromAI},
      {label: 'Submitted to AI', value: jobResult.submittedToAI},
      {label: 'Completed', value: jobResult.completed},
      {label: 'Errored', value: getErroredCount(jobResult)},
    ];

    if (String(jobData.jobType ?? '').toUpperCase() === 'RELEVANCY') {
      summaryFields.push(
        {label: 'Relevant', value: jobResult.relevant},
        {label: 'Not relevant', value: jobResult.nonRelevant}
      );
    }

    summaryFields.forEach(({label, value}) => {
      y = drawLabelValue(
        doc,
        label,
        normalizePdfValue(value),
        margin,
        y,
        contentWidth,
        lineHeight
      );
      y += lineHeight;
    });
  }

  y = drawSectionHeading(
    doc,
    'Protocol Details:',
    margin,
    y + lineHeight,
    lineHeight,
    margin
  );

  y = drawLabelValue(
    doc,
    'Protocol',
    getProtocolLabel(jobData),
    margin,
    y,
    contentWidth,
    lineHeight
  );
  y += lineHeight;

  if (protocolContent) {
    const isRelevancyJob =
      String(jobData.jobType ?? '').toUpperCase() === 'RELEVANCY';
    serializeProtocolPdfContent(protocolContent, {
      includeRelevanceCriteria: isRelevancyJob,
      includeIssueCoding: !isRelevancyJob,
    }).forEach((line) => {
      y = drawProtocolPdfLine(doc, line, y, contentWidth, lineHeight);
    });
  }

  const idStr = `${jobData.jobId ?? ''}`;
  doc.save(`AIReviewJobDetails_${idStr}.pdf`);
}
